import React, { useState, useEffect } from 'react';

// Import broker logos
import tradierLogo from '@/assets/images/brokers/tradier.svg';
import tastytradeLogo from '@/assets/images/brokers/tastytrade.svg';
import schwabLogo from '@/assets/images/brokers/schwab.svg';

interface Broker {
  name: string;
  connected: boolean;
}

const initialBrokers: Broker[] = [
  { name: 'Tradier', connected: false },
  { name: 'Tastytrade', connected: false },
  { name: 'Schwab', connected: false },
];

// Use imported logos
const brokerIcons: Record<string, string> = {
  Tradier: tradierLogo,
  Tastytrade: tastytradeLogo,
  Schwab: schwabLogo,
};

interface BrokerConnectionManagerProps {
  schwabConnected: boolean; // Added
  tradierConnected: boolean; // Added
  tastytradeConnected: boolean; // Added
  loadingSources: Set<string>; // Added
  errorSources: Record<string, string | null>; // Added
  onTradierConnectionChange?: (connected: boolean) => void;
  onSchwabConnectionChange?: (connected: boolean) => void;
  onTastytradeConnectionChange?: (connected: boolean) => void;
}

const BrokerConnectionManager: React.FC<BrokerConnectionManagerProps> = ({ 
  schwabConnected, // Added
  tradierConnected, // Added
  tastytradeConnected, // Added
  loadingSources, // Added
  errorSources, // Added
  onTradierConnectionChange, 
  onSchwabConnectionChange, 
  onTastytradeConnectionChange 
}) => {
  const [brokers, setBrokers] = useState<Broker[]>(initialBrokers);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [apiKeyInput, setApiKeyInput] = useState('');

  useEffect(() => {
    // Check Tradier connection status
    const tradierKey = localStorage.getItem('tradierApiKey');
    if (tradierKey) {
      setBrokers(prev => prev.map(b => b.name === 'Tradier' ? { ...b, connected: true } : b));
      onTradierConnectionChange?.(true);
    } else {
      setBrokers(prev => prev.map(b => b.name === 'Tradier' ? { ...b, connected: false } : b)); // Ensure state is false
      onTradierConnectionChange?.(false);
    }

    // Check Schwab connection status
    const checkSchwabStatus = async () => {
      try {
        const response = await fetch('/api/schwab/status'); // Assuming GET and returns { connected: boolean }
        if (response.ok) {
          const data = await response.json();
          if (data.connected) {
            setBrokers(prev => prev.map(b => b.name === 'Schwab' ? { ...b, connected: true } : b));
            onSchwabConnectionChange?.(true);
          } else {
            setBrokers(prev => prev.map(b => b.name === 'Schwab' ? { ...b, connected: false } : b));
            onSchwabConnectionChange?.(false);
          }
        } else {
          console.error('Failed to fetch Schwab status:', response.statusText);
          setBrokers(prev => prev.map(b => b.name === 'Schwab' ? { ...b, connected: false } : b));
          onSchwabConnectionChange?.(false);
        }
      } catch (error) {
        console.error('Error fetching Schwab status:', error);
        setBrokers(prev => prev.map(b => b.name === 'Schwab' ? { ...b, connected: false } : b));
        onSchwabConnectionChange?.(false);
      }
    };

    // Check TastyTrade connection status
    const checkTastytradeStatus = async () => {
      try {
        console.log("Checking TastyTrade connection status...");
        const response = await fetch('/api/tastytrade/status');
        if (response.ok) {
          const data = await response.json();
          console.log("TastyTrade status response:", data);
          if (data.connected) {
            setBrokers(prev => prev.map(b => b.name === 'Tastytrade' ? { ...b, connected: true } : b));
            onTastytradeConnectionChange?.(true); // Corrected prop name
            console.log("TastyTrade connection state updated to CONNECTED");
          } else {
            setBrokers(prev => prev.map(b => b.name === 'Tastytrade' ? { ...b, connected: false } : b));
            onTastytradeConnectionChange?.(false); // Corrected prop name
            console.log("TastyTrade connection state updated to DISCONNECTED");
          }
        } else {
          console.error('Failed to fetch Tastytrade status:', response.statusText);
          setBrokers(prev => prev.map(b => b.name === 'Tastytrade' ? { ...b, connected: false } : b));
          onTastytradeConnectionChange?.(false); // Corrected prop name
        }
      } catch (error) {
        console.error('Error fetching Tastytrade status:', error);
        setBrokers(prev => prev.map(b => b.name === 'Tastytrade' ? { ...b, connected: false } : b));
        onTastytradeConnectionChange?.(false); // Corrected prop name
      }
    };

    checkSchwabStatus();
    checkTastytradeStatus();
  }, [onTradierConnectionChange, onSchwabConnectionChange, onTastytradeConnectionChange]);

  // New useEffect to synchronize internal state with props
  useEffect(() => {
    setBrokers(prevBrokers => prevBrokers.map(b => {
      if (b.name === 'Schwab') return { ...b, connected: schwabConnected };
      if (b.name === 'Tradier') return { ...b, connected: tradierConnected };
      if (b.name === 'Tastytrade') return { ...b, connected: tastytradeConnected };
      return b;
    }));
  }, [schwabConnected, tradierConnected, tastytradeConnected]);

  const toggleConnection = async (brokerName: string) => {
    if (brokerName === 'Tradier') {
      const isConnected = brokers.find(b => b.name === 'Tradier')?.connected;
      if (isConnected) { // Disconnect Tradier
        localStorage.removeItem('tradierApiKey');
        setBrokers(prev => prev.map(b => b.name === 'Tradier' ? { ...b, connected: false } : b));
        onTradierConnectionChange?.(false);
      } else { // Connect Tradier
        setShowApiKeyModal(true); // Modal handles actual connection and state update via handleApiKeySubmit
      }
      return;
    }

    if (brokerName === 'Schwab') {
      const isConnected = brokers.find(b => b.name === 'Schwab')?.connected;
      if (isConnected) { // Disconnect Schwab
        try {
          const response = await fetch('/api/schwab/logout', { method: 'POST' });
          if (response.ok) {
            setBrokers(prev => prev.map(b => b.name === 'Schwab' ? { ...b, connected: false } : b));
            onSchwabConnectionChange?.(false);
            // Optionally, add a success toast message here
          } else {
            console.error('Schwab logout failed:', await response.text());
            // Optionally, add an error toast message here
          }
        } catch (error) {
          console.error('Error during Schwab logout:', error);
          // Optionally, add an error toast message here
        }
      } else { // Connect Schwab
        try {
          const response = await fetch('/api/schwab/auth-url'); // Fetch the auth URL
          if (response.ok) {
            const data = await response.json();
            if (data.url) {
              window.location.href = data.url; // Redirect to the URL received from the backend
            } else {
              console.error('Schwab auth URL not found in response:', data);
              // Optionally, add an error toast message here
            }
          } else {
            console.error('Failed to fetch Schwab auth URL:', await response.text());
            // Optionally, add an error toast message here
          }
        } catch (error) {
          console.error('Error fetching Schwab auth URL:', error);
          // Optionally, add an error toast message here
        }
      }
      return;
    }
    
    if (brokerName === 'Tastytrade') {
      const isConnected = brokers.find(b => b.name === 'Tastytrade')?.connected;
      if (isConnected) { // Disconnect Tastytrade
        try {
          const response = await fetch('/api/tastytrade/logout', { method: 'POST' });
          if (response.ok) {
            setBrokers(prev => prev.map(b => b.name === 'Tastytrade' ? { ...b, connected: false } : b));
            onTastytradeConnectionChange?.(false); // Corrected prop name
            // Optionally, add a success toast message here
          } else {
            console.error('Tastytrade logout failed:', await response.text());
            // Optionally, add an error toast message here
          }
        } catch (error) {
          console.error('Error during Tastytrade logout:', error);
          // Optionally, add an error toast message here
        }
      } else { // Connect Tastytrade
        try {
          const response = await fetch('/api/tastytrade/auth-url'); // Fetch the auth URL
          if (response.ok) {
            const data = await response.json();
            if (data.url) {
              window.location.href = data.url; // Redirect to the URL received from the backend
            } else {
              console.error('Tastytrade auth URL not found in response:', data);
              // Optionally, add an error toast message here
            }
          } else {
            console.error('Failed to fetch Tastytrade auth URL:', await response.text());
            // Optionally, add an error toast message here
          }
        } catch (error) {
          console.error('Error fetching Tastytrade auth URL:', error);
          // Optionally, add an error toast message here
        }
      }
      return;
    }
  };

  const handlePillClick = (broker: Broker) => {
    toggleConnection(broker.name);
  };

  const handleApiKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (apiKeyInput.trim()) {
      localStorage.setItem('tradierApiKey', apiKeyInput.trim());
      setBrokers(prev => prev.map(b => b.name === 'Tradier' ? { ...b, connected: true } : b));
      onTradierConnectionChange?.(true);
      setShowApiKeyModal(false);
      setApiKeyInput('');
    }
  };

  return (
    <div className="mb-8 bg-white shadow rounded-lg p-6">
      <h2 className="text-2xl font-semibold text-gray-800 mb-6 text-center">Broker Connections</h2>
      <div className="flex flex-row justify-center space-x-6">
        {brokers.map(broker => (
          <div key={broker.name} className="flex flex-col items-center p-6 border rounded-lg shadow-sm hover:shadow-md transition-shadow w-48">
            <img
              src={brokerIcons[broker.name]}
              alt={broker.name + ' logo'}
              className="w-16 h-16 rounded-full bg-gray-100 object-contain border border-gray-200 mb-3"
            />
            <span className="text-gray-800 font-medium text-lg mb-3">{broker.name}</span>
            <button
              className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-400 ${
                broker.connected
                  ? 'bg-green-100 text-green-700 border-green-300 hover:bg-green-200'
                  : 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200'
              }`}
              onClick={() => handlePillClick(broker)}
              aria-pressed={broker.connected}
            >
              {broker.connected ? 'Connected' : 'Connect'}
            </button>
          </div>
        ))}
      </div>
      {showApiKeyModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <form onSubmit={handleApiKeySubmit} className="bg-white p-6 rounded-lg shadow-lg flex flex-col gap-4 min-w-[320px]">
            <h3 className="text-lg font-semibold">Enter Tradier API Key</h3>
            <input
              type="text"
              className="border rounded px-3 py-2"
              placeholder="Paste your Tradier API key (sandbox or production)"
              value={apiKeyInput}
              onChange={e => setApiKeyInput(e.target.value)}
              autoFocus
              required
            />
            <div className="text-xs text-gray-500">
              You can use your <a href="https://sandbox.tradier.com/settings/api" target="_blank" rel="noopener noreferrer" className="underline">Tradier sandbox API key</a> for testing.
            </div>
            <div className="flex gap-2 justify-end">
              <button type="button" className="px-3 py-1 rounded bg-gray-200" onClick={() => setShowApiKeyModal(false)}>Cancel</button>
              <button type="submit" className="px-3 py-1 rounded bg-blue-600 text-white">Connect</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default BrokerConnectionManager;
